import {
  useCurrentCompany,
  useSetRepairersActivity,
  useUpdateCurrentCompany,
} from 'app/core/api/company/api-company';
import {
  CompanyDetailsPt1,
  PageDetails,
} from 'app/core/api/company/api-company.types';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import { ErrorText } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import Form from 'app/shared/frames/Form';
import {
  useEffectOnce,
  useEffectOnChanges,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useObservableEagerState } from 'observable-hooks';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import BottomEditActions from '../create-business-profile/input-pages/components/BottomEditActions';

export default function UpdateBusinessProfile(): JSX.Element {
  useSetPageTitle('Business Profile');
  const history = useHistory();

  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Business Profile',
    };
    repairActivity(pageDetails).subscribe();
  });
  const {
    request$: updateCompany$,
    error: updateError,
    sending: updatingCompany,
  } = useUpdateCurrentCompany();
  const { resource: company, loading } = useCurrentCompany({
    fresh: true,
  });
  const { showSnack } = useShowInfo();

  const editable$ = useMemo(() => new BehaviorSubject(false), []);
  const editable = useObservableEagerState(editable$);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, inputProps, formState } =
    useInputForm<CompanyDetailsPt1>({
      mode: 'onTouched',
      defaultValues: company,
      apiError: updateError,
    });

  // Click cancel button -> get navigated back automatically
  useEffectOnChanges(() => {
    if (!editable && !updatingCompany && !updateError) {
      history.goBack();
    }
  }, [editable, history, updateError, updatingCompany]);

  return (
    <>
      <Body color="primary" bold>
        Business Profile
      </Body>
      <Form
        flexStretch
        onSubmit={handleSubmit(details => {
          if (_.isEmpty(formState.touched)) {
            history.goBack();
            return;
          }
          return updateCompany$(details).pipe(
            tap(() => {
              showSnack({
                message: 'Updated successfully!',
                alertType: 'info',
              });
            })
          );
        })}
      >
        <FormTextInput
          controlName="name"
          placeholder="Legal name of business"
          autoComplete="organization"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="displayName"
          placeholder="Name to appear to consumers"
          autoComplete="organization"
          optional
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="companyNumber"
          autoComplete="organization-name"
          optional
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="vatNumber"
          placeholder="VAT number"
          optional
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="phone"
          placeholder="Business phone number"
          autoComplete="tel"
          optional
          type="tel"
          disabled={!editable}
          {...inputProps}
        />
        <FormTextInput
          controlName="website"
          autoComplete="url"
          inputProps={{ inputMode: 'url' }}
          disabled={!editable}
          optional
          {...inputProps}
        />
        <Body tiny>Asterisks (*) indicate required fields</Body>
        <ErrorText message={updateError} />

        <BottomEditActions
          sending={updatingCompany}
          {...{ editable$, loading }}
        />
      </Form>
    </>
  );
}
